import React from "react";

/**
 * Global route-level error boundary. Prevents a single page crash from
 * white-screening the entire application. Renders a readable panel with
 * the underlying error message + a reload button so users can recover.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to console + optional telemetry hook
    console.error("[ErrorBoundary]", error, info?.componentStack);
    if (typeof window !== "undefined" && window.__ZJ_ERROR_LOG__) {
      try { window.__ZJ_ERROR_LOG__.push({ error: String(error), stack: info?.componentStack, at: new Date().toISOString() }); } catch { /* ignore */ }
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const msg = err instanceof Error ? err.message : String(err ?? "Unknown error");
      return (
        <div data-testid="error-boundary" className="min-h-[70vh] flex items-center justify-center p-6">
          <div className="max-w-lg w-full border border-destructive/40 bg-destructive/5 rounded p-6 space-y-4">
            <div className="text-xs font-mono uppercase tracking-wider text-destructive">Something went wrong</div>
            <div className="text-sm text-foreground">
              This page hit an unexpected error and could not render. Your data is safe — the rest of the app still works.
            </div>
            <pre data-testid="error-boundary-message" className="text-[11px] font-mono bg-background border rounded p-3 overflow-auto max-h-40 whitespace-pre-wrap break-words">
              {msg}
            </pre>
            <div className="flex gap-2">
              <button
                data-testid="error-boundary-reload"
                onClick={this.handleReload}
                className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider bg-primary text-primary-foreground rounded"
              >
                Reload page
              </button>
              <button
                data-testid="error-boundary-back"
                onClick={() => { if (typeof window !== "undefined") window.history.back(); }}
                className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded"
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
